import React, { useEffect, useRef, useState } from 'react';
import { useTimezone } from 'contexts/TimezoneContext';
import { formatDateInput, formatDateTime } from 'utils/date.util';
import { TIMEZONE_OPTIONS } from 'constants/timezones';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  Button,
  Checkbox,
  Flex,
  FormLabel,
  Input,
  Menu,
  MenuButton,
  MenuList,
  Select,
  Progress,
  Text,
  VStack,
  HStack,
  Badge,
  Switch,
  useColorModeValue,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getOrdersSyncStatusApi,
  startOrdersSyncApi,
  getOrdersSettingsApi,
  updateOrdersSettingsApi,
} from 'features/orders/orders.api';
import { setOrdersSyncProgress } from 'features/orders/orders.slice';
import { getStoresListApi } from 'features/stores/stores.api';
import ordersSyncSocket from '../../../app/socketOrdersSync';

import { useColors } from './colors';

const initialProgress = {
  current: 0,
  total: 0,
  percentage: 0,
  status: 'idle',
  message: '',
};

const timezoneLabel = (tz) =>
  TIMEZONE_OPTIONS.find((o) => o.value === tz)?.label || tz?.replace(/_/g, ' ') || 'system default';

const SyncProgressSection = ({ progress, isRunning, progressTrackBg, mutedTextColor }) => (
  <Box>
    <HStack justify="space-between" mb={2}>
      <Text fontSize="sm" fontWeight="medium">
        Progress
      </Text>
      {isRunning && progress.total > 0 && (
        <Text fontSize="sm" fontWeight="semibold" color="blue.500">
          {progress.percentage ?? 0}%
        </Text>
      )}
    </HStack>
    <Progress
      value={progress.percentage ?? 0}
      size="sm"
      borderRadius="md"
      bg={progressTrackBg}
      isIndeterminate={isRunning && progress.phase === 'counting'}
      hasStripe={isRunning}
      isAnimated={isRunning}
    />
    <HStack mt={2} justify="space-between" align="flex-start">
      <Text fontSize="xs" color={mutedTextColor} flex={1}>
        {progress.message || (isRunning ? 'Running…' : 'Idle')}
        {progress.total > 0 && isRunning && (
          <>
            {' '}
            ({progress.current ?? 0} / {progress.total})
          </>
        )}
      </Text>
      <Badge
        colorScheme={
          isRunning
            ? 'blue'
            : progress.status === 'completed'
              ? 'green'
              : progress.status === 'error'
                ? 'red'
                : 'gray'
        }
      >
        {isRunning && progress.total > 0
          ? `${progress.percentage ?? 0}%`
          : progress.status || 'idle'}
      </Badge>
    </HStack>
  </Box>
);

const OrdersSyncSettingsModal = ({ isOpen, onClose, onSyncComplete }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { timezone } = useTimezone();
  const { textColor, mutedTextColor } = useColors();
  const progressTrackBg = useColorModeValue('gray.100', 'whiteAlpha.200');

  const { settings, syncRunning, syncProgress } = useSelector((s) => s.orders);
  const storesList = useSelector((s) => s.stores.list || []);

  const [backfillFromDate, setBackfillFromDate] = useState('');
  const [backfillStoreId, setBackfillStoreId] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleIntervalMinutes, setScheduleIntervalMinutes] = useState(30);
  const [scheduleStoreIds, setScheduleStoreIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [localProgress, setLocalProgress] = useState(initialProgress);
  const prevSyncStatusRef = useRef('idle');
  const syncModeRef = useRef('backfill');
  const toastRef = useRef(toast);
  const onSyncCompleteRef = useRef(onSyncComplete);
  const dispatchRef = useRef(dispatch);
  toastRef.current = toast;
  onSyncCompleteRef.current = onSyncComplete;
  dispatchRef.current = dispatch;

  const progress = syncProgress || localProgress;
  const isRunning = syncRunning || progress.status === 'running';

  const applyProgressState = (data) => {
    if (!data) return;
    setLocalProgress(data);
    dispatch(setOrdersSyncProgress(data));
  };

  const notifyIfTerminalTransition = (data) => {
    if (!data) return;
    const prev = prevSyncStatusRef.current;
    if (data.status === 'completed' && prev === 'running') {
      const isScheduled = syncModeRef.current === 'scheduled';
      toastRef.current({
        title: isScheduled ? 'Scheduled sync complete' : 'Backfill complete',
        status: 'success',
        duration: 4000,
      });
      if (isScheduled) {
        dispatchRef.current(getOrdersSettingsApi());
      }
      onSyncCompleteRef.current?.();
    } else if (data.status === 'error' && prev === 'running') {
      toastRef.current({
        title: data.message || 'Order sync failed',
        status: 'error',
        duration: 6000,
      });
    }
    prevSyncStatusRef.current = data.status || prev;
  };

  useEffect(() => {
    if (!isOpen) {
      prevSyncStatusRef.current = 'idle';
      return;
    }

    const loadData = async () => {
      const [, statusPayload] = await Promise.all([
        dispatch(getOrdersSettingsApi()),
        dispatch(getOrdersSyncStatusApi()).unwrap(),
        dispatch(getStoresListApi()),
      ]);
      if (statusPayload?.lastProgress) {
        const { updatedAt: _u, ...p } = statusPayload.lastProgress;
        applyProgressState(p);
        prevSyncStatusRef.current = p.status || 'idle';
      }
    };

    loadData();
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (settings?.backfillFromDate) {
      setBackfillFromDate(formatDateInput(settings.backfillFromDate, { timezone }));
    }
    setScheduleEnabled(Boolean(settings?.scheduleEnabled));
    setScheduleIntervalMinutes(settings?.scheduleIntervalMinutes ?? 30);
    const savedScheduleStores =
      settings?.scheduleStoreIds?.length > 0
        ? settings.scheduleStoreIds
        : settings?.storeIds || [];
    setScheduleStoreIds(Array.isArray(savedScheduleStores) ? savedScheduleStores : []);
  }, [settings, timezone]);

  useEffect(() => {
    if (!isOpen) return undefined;

    ordersSyncSocket.connect();

    const onProgress = (data) => {
      applyProgressState(data);
      notifyIfTerminalTransition(data);
    };

    ordersSyncSocket.on('orders-sync-progress', onProgress);

    return () => {
      ordersSyncSocket.off('orders-sync-progress', onProgress);
      ordersSyncSocket.disconnect();
    };
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (!isOpen || !isRunning) return undefined;

    const poll = async () => {
      try {
        const data = await dispatch(getOrdersSyncStatusApi()).unwrap();
        if (data?.lastProgress) {
          const { updatedAt: _u, ...p } = data.lastProgress;
          applyProgressState(p);
          notifyIfTerminalTransition(p);
        }
      } catch {
        // ignore transient poll errors
      }
    };

    poll();
    const intervalId = setInterval(poll, 500);
    return () => clearInterval(intervalId);
  }, [isOpen, isRunning, dispatch]);

  const backfillFromValue = backfillFromDate || undefined;

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await dispatch(
        updateOrdersSettingsApi({
          backfillFromDate: backfillFromValue,
          scheduleEnabled,
          scheduleIntervalMinutes: Number(scheduleIntervalMinutes) || 30,
          scheduleStoreIds,
        }),
      ).unwrap();
      toast({ title: 'Settings saved', status: 'success', duration: 3000 });
    } catch (e) {
      toast({ title: e?.message || 'Could not save settings', status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const marketplaceStores = storesList.filter((s) => {
    const name = (s.marketplace?.name || s.marketplace?.altName || '').toLowerCase();
    return name.includes('ebay') || name.includes('amazon');
  });

  const marketplaceLabel = (store) => {
    const name = store.marketplace?.name || store.marketplace?.altName || '';
    return name ? ` (${name})` : '';
  };

  const syncStoreIds = backfillStoreId ? [backfillStoreId] : undefined;
  const scheduledSyncStoreIds = scheduleStoreIds.length ? scheduleStoreIds : undefined;

  const toggleScheduleStore = (storeId) => {
    const id = String(storeId);
    setScheduleStoreIds((prev) =>
      prev.some((s) => String(s) === id)
        ? prev.filter((s) => String(s) !== id)
        : [...prev, id],
    );
  };

  const handleStartBackfill = async () => {
    try {
      syncModeRef.current = 'backfill';
      prevSyncStatusRef.current = 'running';
      await dispatch(
        startOrdersSyncApi({
          mode: 'backfill',
          storeIds: syncStoreIds,
          backfillFromDate: backfillFromValue,
        }),
      ).unwrap();
      const storeLabel = backfillStoreId
        ? marketplaceStores.find((s) => s.id === backfillStoreId)?.name
        : null;
      toast({
        title: storeLabel ? `Backfill started for ${storeLabel}` : 'Backfill started for all stores',
        status: 'info',
        duration: 3000,
      });
    } catch (e) {
      toast({ title: e?.message || 'Could not start sync', status: 'error' });
    }
  };

  const handleStartScheduledSync = async () => {
    try {
      syncModeRef.current = 'scheduled';
      prevSyncStatusRef.current = 'running';
      await dispatch(
        startOrdersSyncApi({ mode: 'scheduled', storeIds: scheduledSyncStoreIds }),
      ).unwrap();
      const storeLabel =
        scheduledSyncStoreIds?.length === 1
          ? marketplaceStores.find((s) => String(s.id) === String(scheduledSyncStoreIds[0]))?.name
          : null;
      toast({
        title: storeLabel
          ? `Scheduled sync started for ${storeLabel}`
          : scheduledSyncStoreIds?.length
            ? `Scheduled sync started for ${scheduledSyncStoreIds.length} stores`
            : 'Scheduled sync started for all stores',
        status: 'info',
        duration: 3000,
      });
    } catch (e) {
      toast({ title: e?.message || 'Could not start sync', status: 'error' });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Order sync settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Tabs variant="enclosed" size="sm">
            <TabList>
              <Tab>Backfill</Tab>
              <Tab>Schedule</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                      Store
                    </Text>
                    <Select
                      size="sm"
                      value={backfillStoreId}
                      onChange={(e) => setBackfillStoreId(e.target.value)}
                    >
                      <option value="">All eBay &amp; Amazon stores</option>
                      {marketplaceStores.map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name || store.label}
                          {marketplaceLabel(store)}
                        </option>
                      ))}
                    </Select>
                    <Text fontSize="xs" color={mutedTextColor} mt={1}>
                      Limit backfill to a single store, or leave as all stores.
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                      Backfill from date
                    </Text>
                    <Input
                      type="date"
                      size="sm"
                      value={backfillFromDate}
                      onChange={(e) => setBackfillFromDate(e.target.value)}
                    />
                    <Text fontSize="xs" color={mutedTextColor} mt={1}>
                      Orders created on or after this calendar day will be fetched (midnight start in{' '}
                      {timezoneLabel(timezone)}). Orders already in the system are skipped — only new
                      orders are added. Run backfill uses this date immediately — saving settings is
                      optional.
                    </Text>
                  </Box>

                  <SyncProgressSection
                    progress={progress}
                    isRunning={isRunning}
                    progressTrackBg={progressTrackBg}
                    mutedTextColor={mutedTextColor}
                  />

                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={handleStartBackfill}
                    isDisabled={isRunning}
                    leftIcon={isRunning ? <Spinner size="xs" /> : undefined}
                  >
                    Run backfill
                  </Button>

                  <Text fontSize="xs" color={mutedTextColor}>
                    {marketplaceStores.length} store(s) with eBay or Amazon marketplace configured.
                  </Text>
                </VStack>
              </TabPanel>
              <TabPanel px={0}>
                <VStack align="stretch" spacing={4}>
                  <HStack justify="space-between">
                    <Text fontSize="sm">Enable scheduled sync</Text>
                    <Switch
                      isChecked={scheduleEnabled}
                      onChange={(e) => setScheduleEnabled(e.target.checked)}
                    />
                  </HStack>
                  <Text fontSize="xs" color={mutedTextColor}>
                    Fetches new orders and updates existing ones when status changes — including
                    cancellations, full or partial refunds, and returns.
                  </Text>
                  <Box>
                    <FormLabel fontSize="sm" fontWeight="medium" mb={1}>
                      Stores
                    </FormLabel>
                    <Menu closeOnSelect={false}>
                      <MenuButton
                        as={Button}
                        variant="outline"
                        size="sm"
                        w="100%"
                        textAlign="left"
                        fontWeight="normal"
                      >
                        {scheduleStoreIds.length > 0
                          ? `${scheduleStoreIds.length} store${scheduleStoreIds.length === 1 ? '' : 's'} selected`
                          : 'All eBay & Amazon stores'}
                      </MenuButton>
                      <MenuList minW="100%" p={3} maxH="240px" overflowY="auto">
                        <VStack align="stretch" spacing={1}>
                          {marketplaceStores.map((store) => {
                            const storeId = String(store.id);
                            const isChecked = scheduleStoreIds.some(
                              (id) => String(id) === storeId,
                            );
                            return (
                              <Checkbox
                                key={storeId}
                                size="sm"
                                isChecked={isChecked}
                                onChange={() => toggleScheduleStore(storeId)}
                              >
                                {store.name || store.label}
                                {marketplaceLabel(store)}
                              </Checkbox>
                            );
                          })}
                        </VStack>
                      </MenuList>
                    </Menu>
                    <Text fontSize="xs" color={mutedTextColor} mt={1}>
                      Leave empty to sync all marketplace stores. Save settings for automatic runs.
                    </Text>
                    {scheduleStoreIds.length > 0 ? (
                      <Flex wrap="wrap" gap={1} mt={2}>
                        {scheduleStoreIds.map((storeId) => {
                          const store = marketplaceStores.find(
                            (s) => String(s.id) === String(storeId),
                          );
                          return store ? (
                            <Badge
                              key={storeId}
                              colorScheme="blue"
                              variant="subtle"
                              fontSize="xs"
                              px={2}
                              py={0.5}
                              borderRadius="md"
                            >
                              {store.name || store.label}
                            </Badge>
                          ) : null;
                        })}
                      </Flex>
                    ) : null}
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                      Interval (minutes)
                    </Text>
                    <Input
                      type="number"
                      size="sm"
                      min={5}
                      value={scheduleIntervalMinutes}
                      onChange={(e) => setScheduleIntervalMinutes(e.target.value)}
                    />
                  </Box>
                  <SyncProgressSection
                    progress={progress}
                    isRunning={isRunning}
                    progressTrackBg={progressTrackBg}
                    mutedTextColor={mutedTextColor}
                  />

                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={handleStartScheduledSync}
                    isDisabled={isRunning}
                    leftIcon={isRunning ? <Spinner size="xs" /> : undefined}
                  >
                    Run scheduled sync now
                  </Button>

                  {settings?.lastIncrementalSyncAt && (
                    <Text fontSize="xs" color={mutedTextColor}>
                      Last scheduled sync:{' '}
                      {formatDateTime(settings.lastIncrementalSyncAt)}
                    </Text>
                  )}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Close
          </Button>
          <Button colorScheme="blue" onClick={handleSaveSettings} isLoading={saving}>
            Save settings
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OrdersSyncSettingsModal;
